import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Divider,
  Alert,
  Grid
} from '@mui/material';
import {
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  NewReleases as NewReleasesIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { InventoryItem } from '../../utils/types';
import ProductCard from '../../components/store/ProductCard';

const productTypeConfig: Record<string, { title: string; icon: React.ReactNode; field: string; color: string }> = {
  featured: {
    title: 'المنتجات المميزة',
    icon: <StarIcon sx={{ mr: 1 }} />,
    field: 'featured',
    color: 'primary'
  },
  'best-sellers': {
    title: 'الأكثر مبيعًا',
    icon: <TrendingUpIcon sx={{ mr: 1 }} />,
    field: 'bestSeller',
    color: 'error'
  },
  latest: {
    title: 'أحدث المنتجات',
    icon: <NewReleasesIcon sx={{ mr: 1 }} />,
    field: 'createdAt',
    color: 'success'
  }
};

const ProductTypePage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const config = type ? productTypeConfig[type] : null;

  useEffect(() => {
    if (!config) {
      setError('نوع المنتج غير معروف');
      setLoading(false);
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const querySnapshot = await getDocs(collection(db, 'inventory'));
        
        let fetchedProducts = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || '',
            sku: data.sku || '',
            barcode: data.barcode || '',
            category: data.category || '',
            categoryId: data.categoryId || '',
            subcategory: data.subcategory || '',
            subcategoryId: data.subcategoryId || '',
            purchasePrice: data.purchasePrice || 0,
            sellingPrice: data.sellingPrice || 0,
            quantity: data.quantity || 0,
            minQuantity: data.minQuantity || 0,
            description: data.description || '',
            supplier: data.supplier || '',
            image: data.image || '',
            imageUrl: data.imageUrl || data.image || '',
            showInStore: data.showInStore !== undefined ? data.showInStore : true,
            featured: data.featured || false,
            bestSeller: data.bestSeller || false,
            createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
            updatedAt: data.updatedAt ? new Date(data.updatedAt.seconds * 1000) : new Date()
          } as InventoryItem;
        });

        // فلترة المنتجات المعروضة في المتجر
        fetchedProducts = fetchedProducts.filter(p => p.showInStore !== false);

        // فلترة حسب النوع
        if (type === 'latest') {
          fetchedProducts.sort((a, b) => {
            const dateA = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
            const dateB = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
            return dateB - dateA;
          });
        } else {
          fetchedProducts = fetchedProducts.filter(p => p[config.field as keyof InventoryItem] === true);
        }

        setProducts(fetchedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('حدث خطأ أثناء تحميل المنتجات');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [type, config]);

  if (!config) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">نوع المنتج غير معروف</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link component={RouterLink} to="/" color="inherit">
          الرئيسية
        </Link>
        <Typography color="text.primary">
          {config.title}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: config.color === 'primary' ? 'primary.main' : config.color === 'error' ? 'error.main' : 'success.main'
          }}
        >
          {config.icon}
          {config.title}
        </Typography>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {!error && products.length === 0 && (
        <Alert severity="info">لا توجد منتجات في هذا القسم حالياً</Alert>
      )}

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {products.map((product) => (
          <Grid item xs={6} sm={6} md={4} lg={3} key={product.id}>
            <ProductCard product={product} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default ProductTypePage;
